import { useState, useEffect, useRef } from 'react';
import { api, formatApiError } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Shield, Loader2, ChevronLeft, ChevronRight, CheckCircle2, XCircle,
  Clock, FileText, AlertTriangle, Eye, Plus,
} from 'lucide-react';
import { Watermark } from '../components/Watermark';
import { ConfidentialBanner } from '../components/ConfidentialBanner';
import { useContentProtection } from '../hooks/useContentProtection';

const DOC_STATUS = {
  pending_review: { label: 'Pendiente', color: 'text-amber-400 bg-amber-500/10' },
  approved: { label: 'Aprobado', color: 'text-emerald-400 bg-emerald-500/10' },
  rejected: { label: 'Rechazado', color: 'text-red-400 bg-red-500/10' },
};

const RESTRICTION_LEVELS = {
  banned: { label: 'Prohibida', color: 'text-red-500' },
  permit_required: { label: 'Requiere Permiso', color: 'text-amber-400' },
  monitoring: { label: 'Monitoreo', color: 'text-blue-400' },
};

export default function AdminCompliancePage() {
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState({});
  const [docs, setDocs] = useState([]);
  const [docsTotal, setDocsTotal] = useState(0);
  const [docsPage, setDocsPage] = useState(1);
  const [docsStatus, setDocsStatus] = useState('pending_review');
  const [species, setSpecies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [addSpeciesDialog, setAddSpeciesDialog] = useState(false);
  const [newSpecies, setNewSpecies] = useState({ species_name: '', species_slug: '', category_slug: '', restriction_level: 'permit_required', nom_059_status: '', cites_appendix: '', notes: '' });
  const protectedRef = useRef(null);
  useContentProtection(protectedRef, { enabled: tab === 'docs' });

  useEffect(() => { loadOverview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'docs') loadDocs(); }, [tab, docsPage, docsStatus]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (tab === 'species') loadSpecies(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOverview = async () => {
    setLoading(true);
    try {
      const data = await api.adminComplianceOverview();
      setOverview(data);
    } catch (e) { console.error('Compliance overview error:', e); }
    finally { setLoading(false); }
  };

  const loadDocs = async () => {
    setLoading(true);
    try {
      const data = await api.adminComplianceDocs({ status: docsStatus, page: docsPage, limit: 20 });
      setDocs(data.docs || []);
      setDocsTotal(data.total || 0);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadSpecies = async () => {
    setLoading(true);
    try {
      const data = await api.adminRestrictedSpecies();
      setSpecies(data.species || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const handleReview = async (approved) => {
    if (!reviewDialog) return;
    try {
      await api.adminReviewDoc(reviewDialog.id, { approved, notes: reviewNotes });
      toast.success(approved ? 'Documento aprobado' : 'Documento rechazado');
      setReviewDialog(null);
      setReviewNotes('');
      loadDocs();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const handleAddSpecies = async () => {
    if (!newSpecies.species_name.trim()) { toast.error('Nombre requerido'); return; }
    try {
      await api.adminAddRestriction(newSpecies);
      toast.success('Restriccion agregada');
      setAddSpeciesDialog(false);
      setNewSpecies({ species_name: '', species_slug: '', category_slug: '', restriction_level: 'permit_required', nom_059_status: '', cites_appendix: '', notes: '' });
      loadSpecies();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const fmt = (v) => v ? new Date(v).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '-';

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="admin-compliance-page" ref={protectedRef}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Compliance / Legal</h1>
          <p className="text-xs text-zinc-600">NOM-059, CITES, documentacion legal</p>
        </div>
      </div>
      {tab === 'docs' && <ConfidentialBanner />}

      {/* Tabs */}
      <div className="flex gap-1 mb-6 p-1 rounded-lg bg-white/[0.02] border border-white/[0.04] w-fit">
        {[
          { id: 'overview', label: 'Resumen' },
          { id: 'docs', label: 'Documentos' },
          { id: 'species', label: 'Especies Restringidas' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-md text-xs transition-colors ${tab === t.id ? 'bg-white/[0.06] text-zinc-100' : 'text-zinc-500 hover:text-zinc-300'}`}
            data-testid={`compliance-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === 'overview' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="compliance-overview">
          {loading ? (
            <div className="col-span-5 text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></div>
          ) : [
            { label: 'Docs Pendientes', value: overview.pending_docs || 0, icon: Clock, color: 'text-amber-400' },
            { label: 'Docs Aprobados', value: overview.approved_docs || 0, icon: CheckCircle2, color: 'text-emerald-400' },
            { label: 'Docs Rechazados', value: overview.rejected_docs || 0, icon: XCircle, color: 'text-red-400' },
            { label: 'Listings Pendientes', value: overview.pending_listings || 0, icon: AlertTriangle, color: 'text-orange-400' },
            { label: 'Especies Restringidas', value: overview.restricted_species || 0, icon: Shield, color: 'text-purple-400' },
          ].map(s => (
            <div key={s.label} className="p-4 rounded-xl border border-white/[0.04] bg-[#0A0A0D]">
              <s.icon className={`w-4 h-4 ${s.color} mb-2`} />
              <p className="text-2xl font-bold text-zinc-100">{s.value}</p>
              <p className="text-[10px] text-zinc-600 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Documents Tab */}
      {tab === 'docs' && (
        <div>
          <div className="flex gap-3 mb-4">
            <Select value={docsStatus} onValueChange={v => { setDocsStatus(v); setDocsPage(1); }}>
              <SelectTrigger className="w-44 bg-white/[0.03] border-white/5 text-zinc-300 h-9 text-sm" data-testid="docs-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121215] border-white/10">
                <SelectItem value="pending_review">Pendientes</SelectItem>
                <SelectItem value="approved">Aprobados</SelectItem>
                <SelectItem value="rejected">Rechazados</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.04]">
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Tipo</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Anuncio</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Vendedor</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Status</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Fecha</th>
                  <th className="text-right px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Accion</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
                ) : docs.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-zinc-600 text-sm">Sin documentos</td></tr>
                ) : docs.map(d => {
                  const sc = DOC_STATUS[d.status] || DOC_STATUS.pending_review;
                  return (
                    <tr key={d.id} className="border-b border-white/[0.02] hover:bg-white/[0.01]" data-testid={`doc-row-${d.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <FileText className="w-3.5 h-3.5 text-zinc-600" />
                          <span className="text-zinc-200 text-xs">{d.doc_type || 'Documento'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3"><span className="text-zinc-300 text-xs truncate max-w-[150px] block">{d.listing_title || d.listing_id?.slice(0, 8)}</span></td>
                      <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{d.seller_name || '-'}</span></td>
                      <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full ${sc.color}`}>{sc.label}</span></td>
                      <td className="px-4 py-3"><span className="text-[10px] text-zinc-600">{fmt(d.uploaded_at)}</span></td>
                      <td className="px-4 py-3 text-right">
                        {d.status === 'pending_review' ? (
                          <Button size="sm" variant="ghost" onClick={() => { setReviewDialog(d); setReviewNotes(''); }}
                            className="h-7 text-[10px] text-amber-400 hover:bg-amber-500/10" data-testid={`review-doc-${d.id}`}>
                            <Eye className="w-3 h-3 mr-1" /> Revisar
                          </Button>
                        ) : (
                          <span className="text-[10px] text-zinc-700">{d.review_notes || '-'}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.04]">
              <span className="text-xs text-zinc-600">Pagina {docsPage} de {Math.ceil(docsTotal / 20) || 1}</span>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" disabled={docsPage === 1} onClick={() => setDocsPage(p => p - 1)} className="h-7 text-xs text-zinc-400"><ChevronLeft className="w-3 h-3" /></Button>
                <Button size="sm" variant="ghost" disabled={docs.length < 20} onClick={() => setDocsPage(p => p + 1)} className="h-7 text-xs text-zinc-400"><ChevronRight className="w-3 h-3" /></Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Species Tab */}
      {tab === 'species' && (
        <div>
          <div className="flex justify-end mb-4">
            <Button onClick={() => setAddSpeciesDialog(true)}
              className="bg-purple-600 hover:bg-purple-500 text-white h-9 text-xs" data-testid="add-restriction-btn">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Agregar Restriccion
            </Button>
          </div>

          <div className="rounded-xl border border-white/[0.04] bg-[#0A0A0D] overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/[0.04]">
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Especie</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Categoria</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Nivel</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">NOM-059</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">CITES</th>
                  <th className="text-left px-4 py-3 text-[10px] text-zinc-600 uppercase tracking-wider font-medium">Notas</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={6} className="text-center py-12"><Loader2 className="w-5 h-5 text-zinc-600 animate-spin mx-auto" /></td></tr>
                ) : species.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-12 text-zinc-600 text-sm">Sin restricciones configuradas</td></tr>
                ) : species.map(s => {
                  const rl = RESTRICTION_LEVELS[s.restriction_level] || RESTRICTION_LEVELS.monitoring;
                  return (
                    <tr key={s.id} className="border-b border-white/[0.02] hover:bg-white/[0.01]" data-testid={`species-row-${s.id}`}>
                      <td className="px-4 py-3"><span className="text-zinc-200 text-xs">{s.species_name}</span></td>
                      <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{s.category_slug}</span></td>
                      <td className="px-4 py-3"><span className={`text-xs font-medium ${rl.color}`}>{rl.label}</span></td>
                      <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{s.nom_059_status || '-'}</span></td>
                      <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{s.cites_appendix || '-'}</span></td>
                      <td className="px-4 py-3"><span className="text-zinc-600 text-[10px] truncate max-w-[150px] block">{s.notes || '-'}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Review Dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-md" data-testid="review-doc-dialog">
          <DialogHeader><DialogTitle className="text-zinc-100">Revisar Documento</DialogTitle></DialogHeader>
          {reviewDialog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[10px] text-zinc-600 block">Tipo</span><span className="text-zinc-200">{reviewDialog.doc_type}</span></div>
                <div><span className="text-[10px] text-zinc-600 block">Anuncio</span><span className="text-zinc-200">{reviewDialog.listing_title || reviewDialog.listing_id?.slice(0, 8)}</span></div>
              </div>
              {reviewDialog.file_url && (
                <a href={reviewDialog.file_url} target="_blank" rel="noreferrer" className="text-purple-400 text-xs hover:underline block">Ver archivo adjunto</a>
              )}
              <div>
                <label className="text-xs text-zinc-500 mb-1.5 block">Notas de revision</label>
                <Input value={reviewNotes} onChange={e => setReviewNotes(e.target.value)}
                  placeholder="Notas opcionales..." className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" data-testid="review-notes-input" />
              </div>
              <div className="flex gap-2 justify-end">
                <Button onClick={() => handleReview(false)} className="bg-red-600 hover:bg-red-500 text-white h-9 text-xs" data-testid="reject-doc-btn">
                  <XCircle className="w-3.5 h-3.5 mr-1.5" /> Rechazar
                </Button>
                <Button onClick={() => handleReview(true)} className="bg-emerald-600 hover:bg-emerald-500 text-white h-9 text-xs" data-testid="approve-doc-btn">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Aprobar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Species Dialog */}
      <Dialog open={addSpeciesDialog} onOpenChange={setAddSpeciesDialog}>
        <DialogContent className="bg-[#121215] border-white/10 max-w-md" data-testid="add-species-dialog">
          <DialogHeader><DialogTitle className="text-zinc-100">Agregar Restriccion de Especie</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Nombre de Especie *</label>
              <Input value={newSpecies.species_name} onChange={e => setNewSpecies(p => ({ ...p, species_name: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" data-testid="species-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Slug</label>
                <Input value={newSpecies.species_slug} onChange={e => setNewSpecies(p => ({ ...p, species_slug: e.target.value }))}
                  placeholder="ej: boa-constrictor" className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">Categoria</label>
                <Input value={newSpecies.category_slug} onChange={e => setNewSpecies(p => ({ ...p, category_slug: e.target.value }))}
                  placeholder="ej: boas" className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Nivel de Restriccion</label>
              <Select value={newSpecies.restriction_level} onValueChange={v => setNewSpecies(p => ({ ...p, restriction_level: v }))}>
                <SelectTrigger className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" data-testid="restriction-level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121215] border-white/10">
                  <SelectItem value="banned">Prohibida</SelectItem>
                  <SelectItem value="permit_required">Requiere Permiso</SelectItem>
                  <SelectItem value="monitoring">Monitoreo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">NOM-059 Status</label>
                <Input value={newSpecies.nom_059_status} onChange={e => setNewSpecies(p => ({ ...p, nom_059_status: e.target.value }))}
                  placeholder="ej: En peligro" className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" />
              </div>
              <div>
                <label className="text-xs text-zinc-500 mb-1 block">CITES Apendice</label>
                <Input value={newSpecies.cites_appendix} onChange={e => setNewSpecies(p => ({ ...p, cites_appendix: e.target.value }))}
                  placeholder="ej: I, II, III" className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" />
              </div>
            </div>
            <div>
              <label className="text-xs text-zinc-500 mb-1 block">Notas</label>
              <Input value={newSpecies.notes} onChange={e => setNewSpecies(p => ({ ...p, notes: e.target.value }))}
                className="bg-white/5 border-white/10 text-zinc-100 h-9 text-sm" />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setAddSpeciesDialog(false)} className="border-white/10 text-zinc-400 h-9 text-xs">Cancelar</Button>
              <Button onClick={handleAddSpecies} className="bg-purple-600 hover:bg-purple-500 text-white h-9 text-xs" data-testid="submit-restriction-btn">Agregar</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
